import React, { useState } from 'react';
import Notification from '../../notification';
import CustomSelect from '../../layout/CustomSelect';
import { dashboard as dashboardSelectStyle, dashboardSelectClassNames } from '../../../utils/customSelectStyle';
import { addBankAccountAndFund } from '../../../utils/paymentBanks';
import { isValidFundsAmount } from '../../../utils/validators';

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Checking', value: 'checking' },
  { label: 'Savings', value: 'savings' },
];

export default function AddBankAccount({
  email,
  selectedCard,
  amount,
  onSuccess,
  openVerificationPopup,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [saveBankAccountForLater, setSaveBankAccountForLater] = useState(false);
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountType, setAccountType] = useState(ACCOUNT_TYPE_OPTIONS[0]);
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const handleRoutingNumber = (e) => {
    setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9));
  };

  const handleAccountNumber = (e) => {
    setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 17));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedCard) return;

    if (!bankName.trim()) {
      Notification('error', { message: 'Please enter the bank name' });
      return;
    }
    if (!accountHolderName.trim()) {
      Notification('error', { message: 'Please enter the account holder name' });
      return;
    }
    if (routingNumber.length !== 9) {
      Notification('error', { message: 'Routing number must be exactly 9 digits' });
      return;
    }
    if (accountNumber.length < 4) {
      Notification('error', { message: 'Please enter a valid account number' });
      return;
    }
    if (!isValidFundsAmount(amount)) {
      Notification('error', { message: 'Please enter an amount of at least $0.01' });
      return;
    }

    setSubmitting(true);
    try {
      const returnUrl = `${window.location.origin}/verification-complete`;
      const data = await addBankAccountAndFund({
        amount: Number(amount),
        bankName,
        accountHolderName,
        accountType: accountType.value,
        routingNumber,
        accountNumber,
        email,
        saveBankAccount: saveBankAccountForLater,
        returnUrl,
      });

      const verificationUrl = data?.nextAction?.verify_with_microdeposits?.hosted_verification_url;
      if (verificationUrl && openVerificationPopup) {
        openVerificationPopup(verificationUrl);
      }

      Notification('success', { message: data?.message || 'Bank account added successfully!' });
      onSuccess();
    } catch (err) {
      Notification('error', {
        message: err.response?.data?.error || err.response?.data?.message || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = !!selectedCard || submitting;

  return (
    <form id="bankAccountForm" onSubmit={handleSubmit}>
      <div className="field_col">
        <label className="field_name" htmlFor="bankName">
          Bank Name
        </label>
        <input
          id="bankName"
          className="dashboard-input"
          type="text"
          placeholder="e.g. Chase, Bank of America"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="accountHolderName">
          Account Holder Name
        </label>
        <input
          id="accountHolderName"
          className="dashboard-input"
          type="text"
          placeholder="Full name on bank account"
          value={accountHolderName}
          onChange={(e) => setAccountHolderName(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="account-type-select">
          Account Type
        </label>
        <div className="dashboard-select-wrap">
          <CustomSelect
            inputId="account-type-select"
            styles={dashboardSelectStyle}
            classNames={dashboardSelectClassNames}
            options={ACCOUNT_TYPE_OPTIONS}
            value={accountType}
            onChange={(option) => setAccountType(option)}
            isSearchable={false}
            isDisabled={disabled}
          />
        </div>
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="routingNumber">
          Routing Number
        </label>
        <input
          id="routingNumber"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="9-digit routing number"
          value={routingNumber}
          onChange={handleRoutingNumber}
          maxLength={9}
          disabled={disabled}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="accountNumber">
          Account Number
        </label>
        <input
          id="accountNumber"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="Bank account number"
          value={accountNumber}
          onChange={handleAccountNumber}
          maxLength={17}
          disabled={disabled}
        />
      </div>

      <label className="check_btn" htmlFor="saveBankAccount">
        Save bank account for future use
        <input
          type="checkbox"
          name="saveBankAccount"
          id="saveBankAccount"
          checked={saveBankAccountForLater}
          onChange={(e) => setSaveBankAccountForLater(e.target.checked)}
          disabled={disabled}
        />
      </label>
    </form>
  );
}
